import React, { useState } from "react";
import { SpotActivityMarker } from "../SpotActivityMarker";
import {
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  Slider,
  Switch,
} from "@mui/material";
import { formatDate, onlyUniqueValues } from "../../util/util";
import {
  LocationData,
  SpotData,
  SpotMarker,
  TideWindData,
  WindowSize,
} from "../../datastructures";
import { useStaticTableDataFromRemote } from "../../util/useStaticTableDataFromRemote";
import { MapContainer, ScaleControl, TileLayer } from "react-leaflet";
import { WindArrowMarker } from "../WindArrowMarker";
import { useRemoteData } from "../../util/useRemoteData";
import MenuIcon from "@mui/icons-material/Menu";
import ConditionalRender from "../ConditionalRender";
import ZoomLevelTracker from "../ZoomLevelTracker";
import { TideMarker } from "../TideMarker";
import { SpotLocationLine } from "../SpotLocationLine";

interface SoarcastMapProperties {
  spotData: SpotData[];
  locationData: LocationData[];
  windowSize: WindowSize;
  windUnits: string;
  tideWindData: TideWindData[];
  canaryMode: boolean;
  markerVisibility: {
    spots: boolean;
    wind: boolean;
    tide: boolean;
  };
  setMarkerVisibility: (v: {
    spots: boolean;
    wind: boolean;
    tide: boolean;
  }) => void;
}

export const SoarcastMap: React.FC<SoarcastMapProperties> = ({
  spotData,
  locationData,
  windowSize,
  windUnits,
  tideWindData,
  canaryMode,
  markerVisibility,
  setMarkerVisibility,
}) => {
  const [etimeToShow, setEtimeToShow] = useState(0);
  const defaultProps = {
    center: { lat: 52.458, lng: 4.548 },
    zoom: 8,
  };
  // Marker visibility state
  const [showMenu, setShowMenu] = useState(false);

  const unfilteredMvMarkersData =
    useStaticTableDataFromRemote<SpotMarker>("table=mv_markers");
  //const startNow = Math.round(Date.now() / 1000) + 3600 > startTimestampSecs;
  const nowTimestampSecs = Math.round(Date.now() / 1000);
  const endTimestampSecs = nowTimestampSecs + 3600 * 24;
  const fTime = (timestampSecs: number): string =>
    formatDate(timestampSecs, "T");

  const spotDataForSpot = (spotId: number): SpotData | undefined =>
    spotData.find((s) => s.id === spotId);

  const kiteAndSoarSpots = spotData.filter((s) => s.kite || s.soar);

  const times = unfilteredMvMarkersData
    ?.map((row) => row.etime)
    ?.filter(onlyUniqueValues)
    ?.filter((t) => t === 0 || (t >= nowTimestampSecs && t < endTimestampSecs))
    ?.sort();

  const fake0Time = times ? times[1] - 3600 : 0;
  const fake0Times = times?.map((t) => (t === 0 ? fake0Time : t));
  const formattedTime = (t: number) =>
    fake0Times ? (t === fake0Times[0] ? "Meting" : fTime(t)) : 0;

  const marks = fake0Times
    ?.map((t) => ({
      value: t,
      label: formattedTime(t),
    }))
    .filter((element, index) => index % 3 === 0);

  //Slider docs see https://mui.com/material-ui/api/slider/

  const spotActivityMarkersForTime = unfilteredMvMarkersData
    .filter(
      (m) =>
        spotDataForSpot(m.spot_id)?.kite || spotDataForSpot(m.spot_id)?.soar,
    )
    ?.filter((m) => m.etime === etimeToShow);

  // Fetch measurement data for etime=0
  const measurementData = useRemoteData<any[]>(
    etimeToShow === 0
      ? "/sc/getTableDump.php?table=mv_measurement_location_markers&has_forecast=true"
      : null,
    [etimeToShow],
  );
  // Fetch forecast data for etimeToShow > 0
  const forecastData = useRemoteData<any[]>(
    etimeToShow !== 0
      ? `/sc/getTableDump.php?table=mv_forecast_location_markers&etime=${etimeToShow}`
      : null,
    [etimeToShow],
  );

  const onSliderValChange = (event: Event, newValue: number | number[]) => {
    const v = Array.isArray(newValue) ? newValue[0] : newValue;
    setEtimeToShow(v === fake0Time ? 0 : v);
  };

  // Filter for spots with valid tide coordinates
  const tideSpots = spotData.filter(
    (s) =>
      s.kf_actual_n !== undefined &&
      s.kf_actual_e !== undefined &&
      s.kf_actual_n !== null &&
      s.kf_actual_e !== null &&
      !isNaN(s.kf_actual_n) &&
      !isNaN(s.kf_actual_e),
  );

  const SpotLocationLines: React.FC = () => {
    if (!markerVisibility.spots || !markerVisibility.wind) return null;
    return (
      <>
        {kiteAndSoarSpots.map((spot) => {
          if (!spot.wind_location) return null;
          const location = locationData.find(
            (l) => l.id === spot.wind_location,
          );
          if (!location) return null;
          return (
            <SpotLocationLine
              key={"line_" + spot.id + "_" + location.id}
              spot={spot}
              location={location}
            />
          );
        })}
      </>
    );
  };

  // Return statement of SoarcastMap react component
  return (
    <>
      {/* Map menu button - only show in canaryMode */}
      <ConditionalRender condition={canaryMode}>
        <IconButton
          style={{
            position: "absolute",
            bottom: 24,
            right: 24,
            zIndex: 1200,
            background: "white",
          }}
          onClick={() => setShowMenu(true)}
          size="large"
        >
          <MenuIcon />
        </IconButton>
        {/* Drawer menu for toggling marker categories */}
        <Drawer
          anchor="right"
          open={showMenu}
          onClose={() => setShowMenu(false)}
        >
          <div style={{ width: 260, padding: 16 }}>
            <h3>Toon op kaart</h3>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={markerVisibility.spots}
                    onChange={(_, v) =>
                      setMarkerVisibility({ ...markerVisibility, spots: v })
                    }
                  />
                }
                label="Activiteiten op spot"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={markerVisibility.wind}
                    onChange={(_, v) =>
                      setMarkerVisibility({ ...markerVisibility, wind: v })
                    }
                  />
                }
                label="Wind meters / voorspel locaties"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={markerVisibility.tide}
                    onChange={(_, v) =>
                      setMarkerVisibility({ ...markerVisibility, tide: v })
                    }
                  />
                }
                label="Getijden smileys"
              />
            </FormGroup>
          </div>
        </Drawer>
      </ConditionalRender>
      <div style={{ padding: "0 2rem" }}>
        <Slider
          aria-label="Custom marks"
          defaultValue={0}
          min={fake0Times ? fake0Times[0] : 0}
          max={endTimestampSecs}
          getAriaValueText={(val) => fTime(val)}
          valueLabelFormat={formattedTime}
          getAriaLabel={fTime}
          step={3600}
          valueLabelDisplay="on"
          marks={marks}
          onChange={onSliderValChange}
        />
      </div>
      <MapContainer
        center={defaultProps.center}
        zoom={defaultProps.zoom}
        scrollWheelZoom={true}
        style={{ height: windowSize.innerHeight - 120, width: "100%" }}
      >
        <ZoomLevelTracker
          initialZoom={defaultProps.zoom}
          canaryMode={canaryMode}
        />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
        />
        <ScaleControl position="bottomleft" />
        {/* Show wind measurement arrows if etimeToShow is 0 */}
        {etimeToShow === 0 &&
          (!measurementData ||
            !Array.isArray(measurementData) ||
            measurementData.length === 0) && (
            <div
              style={{
                position: "absolute",
                top: 150,
                left: 20,
                zIndex: 1000,
                background: "rgba(255,255,255,0.8)",
                padding: 8,
                borderRadius: 4,
              }}
            >
              No wind measurement data available.
            </div>
          )}
        {/* WindArrowMarkers - only show in canaryMode */}
        <ConditionalRender condition={canaryMode && markerVisibility.wind}>
          <>
            {etimeToShow === 0 &&
              measurementData &&
              Array.isArray(measurementData) &&
              measurementData.map((m) => (
                <WindArrowMarker
                  key={"wind_" + m.location_id}
                  n={m.n}
                  e={m.e}
                  location_id={m.location_id}
                  windsnelheid={m.windsnelheid}
                  windrichting={m.windrichting}
                  windstoot={m.windstoot}
                  windUnits={windUnits}
                  location_name={m.location_name}
                  location_source={m.location_source}
                  timestampSecs={0}
                  isMeasurement={true}
                />
              ))}
            {etimeToShow !== 0 &&
              forecastData &&
              Array.isArray(forecastData) &&
              forecastData.map((f) => (
                <WindArrowMarker
                  key={"forecast_" + f.location_id}
                  n={f.n}
                  e={f.e}
                  location_id={f.location_id}
                  windsnelheid={f.windsnelheid}
                  windrichting={f.windrichting}
                  windstoot={f.windstoot}
                  windUnits={windUnits}
                  location_name={f.location_name}
                  location_source={f.location_source}
                  timestampSecs={etimeToShow}
                  isMeasurement={false}
                />
              ))}
          </>
        </ConditionalRender>
        {/* Existing spot markers */}
        <ConditionalRender condition={markerVisibility.spots}>
          {spotActivityMarkersForTime ? (
            spotActivityMarkersForTime.map((m) => (
              <SpotActivityMarker
                key={"spot_" + m.spot_id + "_" + m.etime + "_" + m.iconnr}
                marker={m}
              />
            ))
          ) : (
            <></>
          )}
        </ConditionalRender>
        {/* Lines connecting spot markers to their wind locations - only show in canaryMode */}
        <ConditionalRender
          condition={
            canaryMode && markerVisibility.spots && markerVisibility.wind
          }
        >
          <SpotLocationLines />
        </ConditionalRender>
        {/* T-markers for tide locations - only show in canaryMode */}
        <ConditionalRender condition={canaryMode && markerVisibility.tide}>
          {tideSpots.map((spot: SpotData) => (
            <TideMarker
              key={"tide_" + spot.id}
              tideWindData={tideWindData}
              etimeToShow={etimeToShow}
              nowTimestampSecs={nowTimestampSecs}
              spot={spot}
              windUnits={windUnits}
            />
          ))}
        </ConditionalRender>
      </MapContainer>
    </>
  );
};
